/**
 * The campaign and asset entities, and the state machines that govern them
 * (spec §12, §16, §17, §53).
 *
 * Three status vocabularies, because they answer three different questions:
 *
 *  - CampaignStatus       where the whole campaign is in review/publish.
 *  - CampaignAssetStatus  where ONE asset is in review/publish.
 *  - AssetGenerationStatus the fine-grained generation pipeline of an asset
 *                          while it is being made (queued -> ... -> ready).
 *
 * The transitions are data, and the guards are pure functions, so control-api,
 * the admin and any job runner agree on what is legal - a publish worker and a
 * screen never disagree about whether an asset may move to PUBLISHED. One
 * failed asset never invalidates the campaign (spec §12): a campaign with a mix
 * of READY and FAILED assets is READY_FOR_REVIEW with an isolated failure, not
 * FAILED.
 *
 * Versioning is immutable (spec §17): a regeneration creates a new
 * CampaignAssetVersion with a parent pointer; prior media is never overwritten,
 * and the tenant chooses the active version. Published history stays
 * reproducible because each version pins its own snapshots and provider run.
 */

import { z } from 'zod';
import { CampaignObjectiveSchema } from './catalog.js';

// ---------------------------------------------------------------------------
// Status vocabularies
// ---------------------------------------------------------------------------

export const CAMPAIGN_STATUSES = [
  'DRAFT',
  'GENERATING',
  'READY_FOR_REVIEW',
  'APPROVED',
  'SCHEDULED',
  'PARTIALLY_PUBLISHED',
  'PUBLISHED',
  'ARCHIVED',
  'FAILED',
] as const;
export const CampaignStatusSchema = z.enum(CAMPAIGN_STATUSES);
export type CampaignStatus = z.infer<typeof CampaignStatusSchema>;

export const CAMPAIGN_ASSET_STATUSES = [
  'GENERATING',
  'READY',
  'NEEDS_REVIEW',
  'APPROVED',
  'SCHEDULED',
  'PUBLISHED',
  'FAILED',
  'ARCHIVED',
] as const;
export const CampaignAssetStatusSchema = z.enum(CAMPAIGN_ASSET_STATUSES);
export type CampaignAssetStatus = z.infer<typeof CampaignAssetStatusSchema>;

/** The generation pipeline (spec §12), distinct from the review workflow. */
export const ASSET_GENERATION_STATUSES = [
  'QUEUED',
  'PLANNING',
  'GENERATING',
  'RENDERING',
  'VALIDATING',
  'READY',
  'FAILED',
  'CANCELLED',
] as const;
export const AssetGenerationStatusSchema = z.enum(ASSET_GENERATION_STATUSES);
export type AssetGenerationStatus = z.infer<typeof AssetGenerationStatusSchema>;

// ---------------------------------------------------------------------------
// Transition graphs
// ---------------------------------------------------------------------------

const CAMPAIGN_TRANSITIONS: Readonly<Record<CampaignStatus, readonly CampaignStatus[]>> = {
  DRAFT: ['GENERATING', 'ARCHIVED'],
  GENERATING: ['READY_FOR_REVIEW', 'FAILED'],
  READY_FOR_REVIEW: ['GENERATING', 'APPROVED', 'SCHEDULED', 'PARTIALLY_PUBLISHED', 'PUBLISHED', 'ARCHIVED'],
  APPROVED: ['READY_FOR_REVIEW', 'GENERATING', 'SCHEDULED', 'PARTIALLY_PUBLISHED', 'PUBLISHED', 'ARCHIVED'],
  SCHEDULED: ['READY_FOR_REVIEW', 'PARTIALLY_PUBLISHED', 'PUBLISHED', 'ARCHIVED'],
  PARTIALLY_PUBLISHED: ['PUBLISHED', 'ARCHIVED'],
  PUBLISHED: ['ARCHIVED'],
  FAILED: ['GENERATING', 'ARCHIVED'],
  ARCHIVED: [],
};

const ASSET_TRANSITIONS: Readonly<Record<CampaignAssetStatus, readonly CampaignAssetStatus[]>> = {
  GENERATING: ['READY', 'FAILED'],
  READY: ['NEEDS_REVIEW', 'APPROVED', 'GENERATING', 'ARCHIVED'],
  NEEDS_REVIEW: ['APPROVED', 'GENERATING', 'ARCHIVED'],
  APPROVED: ['NEEDS_REVIEW', 'GENERATING', 'SCHEDULED', 'PUBLISHED', 'ARCHIVED'],
  SCHEDULED: ['APPROVED', 'PUBLISHED', 'ARCHIVED'],
  PUBLISHED: ['ARCHIVED'],
  FAILED: ['GENERATING', 'ARCHIVED'],
  ARCHIVED: [],
};

export function canTransitionCampaign(from: CampaignStatus, to: CampaignStatus): boolean {
  return CAMPAIGN_TRANSITIONS[from].includes(to);
}
export function nextCampaignStatuses(from: CampaignStatus): readonly CampaignStatus[] {
  return CAMPAIGN_TRANSITIONS[from];
}
export function isTerminalCampaignStatus(s: CampaignStatus): boolean {
  return CAMPAIGN_TRANSITIONS[s].length === 0;
}

export function canTransitionAsset(from: CampaignAssetStatus, to: CampaignAssetStatus): boolean {
  return ASSET_TRANSITIONS[from].includes(to);
}
export function nextAssetStatuses(from: CampaignAssetStatus): readonly CampaignAssetStatus[] {
  return ASSET_TRANSITIONS[from];
}
export function isTerminalAssetStatus(s: CampaignAssetStatus): boolean {
  return ASSET_TRANSITIONS[s].length === 0;
}

/**
 * Roll a set of asset statuses up to the campaign status they imply, once
 * generation is done. A single FAILED among READY/NEEDS_REVIEW does NOT fail
 * the campaign (spec §12); it stays reviewable with an isolated failure. Used
 * to keep the campaign header (mockup) honest without a stored duplicate.
 */
export function rollUpCampaignStatus(assetStatuses: readonly CampaignAssetStatus[]): CampaignStatus {
  if (assetStatuses.length === 0) return 'DRAFT';
  const has = (s: CampaignAssetStatus) => assetStatuses.includes(s);
  const all = (s: CampaignAssetStatus) => assetStatuses.every((x) => x === s);

  if (assetStatuses.some((s) => s === 'GENERATING')) return 'GENERATING';
  if (all('ARCHIVED')) return 'ARCHIVED';
  if (all('FAILED')) return 'FAILED';

  const live = assetStatuses.filter((s) => s !== 'ARCHIVED');
  if (live.length > 0 && live.every((s) => s === 'PUBLISHED')) return 'PUBLISHED';
  if (has('PUBLISHED')) return 'PARTIALLY_PUBLISHED';
  if (live.length > 0 && live.every((s) => s === 'SCHEDULED' || s === 'PUBLISHED')) return 'SCHEDULED';
  if (live.length > 0 && live.every((s) => s === 'APPROVED' || s === 'SCHEDULED' || s === 'PUBLISHED')) return 'APPROVED';
  return 'READY_FOR_REVIEW';
}

// ---------------------------------------------------------------------------
// Regeneration scopes (spec §16)
// ---------------------------------------------------------------------------

export const REGENERATION_SCOPES = [
  'VISUAL_ONLY',
  'NEW_COMPOSITION',
  'NEW_STYLE',
  'DIFFERENT_PHOTO',
  'ENTIRE_ASSET',
  'CREATE_VARIANT',
] as const;
export const RegenerationScopeSchema = z.enum(REGENERATION_SCOPES);
export type RegenerationScope = z.infer<typeof RegenerationScopeSchema>;

// ---------------------------------------------------------------------------
// Entities
// ---------------------------------------------------------------------------

export const CampaignSchema = z
  .object({
    id: z.string().min(1),
    tenantId: z.string().min(1),
    auctionId: z.string().min(1),
    name: z.string().min(1),
    objective: CampaignObjectiveSchema,
    status: CampaignStatusSchema,
    createdBy: z.string().min(1),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
  })
  .strict();
export type Campaign = z.infer<typeof CampaignSchema>;

/** An asset within a campaign - one output slot (a Facebook 4:5 graphic, a
 *  Reels video). It has a workflow status and points at its active version. */
export const CampaignAssetSchema = z
  .object({
    id: z.string().min(1),
    campaignId: z.string().min(1),
    creativeSpecId: z.string().min(1),
    name: z.string().min(1),
    status: CampaignAssetStatusSchema,
    /** The version the tenant has made active/preferred (spec §17). */
    activeVersionId: z.string().min(1).optional(),
    /** Marked when a snapshot the asset was built from has drifted (spec §35). */
    stale: z.boolean().optional(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
  })
  .strict();
export type CampaignAsset = z.infer<typeof CampaignAssetSchema>;

/** One immutable rendered version of an asset. Regeneration appends one of
 *  these; prior media is never overwritten (spec §17). Carries the provenance
 *  needed to reproduce it (spec §44). */
export const CampaignAssetVersionSchema = z
  .object({
    id: z.string().min(1),
    assetId: z.string().min(1),
    version: z.number().int().positive(),
    parentVersionId: z.string().min(1).nullable(),
    /** Why this version exists: initial generation, or a regeneration scope. */
    reason: z.union([z.literal('INITIAL'), RegenerationScopeSchema]),
    generationStatus: AssetGenerationStatusSchema,
    creativeSpecId: z.string().min(1),
    templateVersion: z.number().int().positive().optional(),
    brandSnapshotId: z.string().min(1),
    auctionTruthSnapshotId: z.string().min(1),
    /** The provider run that produced this version, when a provider was used. */
    providerRunId: z.string().min(1).optional(),
    /** The stored rendered output, once READY. */
    storageKey: z.string().min(1).optional(),
    createdBy: z.string().min(1),
    createdAt: z.string().datetime({ offset: true }),
  })
  .strict();
export type CampaignAssetVersion = z.infer<typeof CampaignAssetVersionSchema>;
